import React, { useEffect } from 'react';
import { X, Settings } from 'lucide-react';
import { useLocale } from '../../../contexts/LocaleContext';

export default function PluginConfigDrawer({ plugin, isOpen, onClose, children }) {
  const { t } = useLocale();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Split the ESC hint around the <kbd> tag
  const [escBefore, escAfter] = t('plugins.pressEscToClose').split('{key}');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] transition-opacity duration-300"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', opacity: isOpen ? 1 : 0 }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full z-[101] flex flex-col shadow-2xl transition-transform duration-300 ease-out"
        style={{
          width: 'min(600px, 100vw)',
          backgroundColor: 'var(--bg-primary)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' }}
            >
              <Settings className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                {t('plugins.configurePlugin')}
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {plugin?.label || plugin?.name}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
            style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-tertiary)' }}
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.close')}</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: 'var(--bg-primary)' }}>
          <div className="p-6">{children}</div>
        </div>

        {/* Footer hint */}
        <div
          className="px-6 py-3 border-t text-xs shrink-0"
          style={{
            borderColor: 'var(--border-color)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-tertiary)',
          }}
        >
          {escBefore}
          <kbd
            className="px-1.5 py-0.5 rounded font-mono text-[10px]"
            style={{ backgroundColor: 'var(--bg-tertiary)' }}
          >
            ESC
          </kbd>
          {escAfter}
        </div>
      </div>
    </>
  );
}